import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type AttentionStatus } from '@/api/client'

export const REVIEW_STATS_KEY = ['reviews', 'stats'] as const
export const REVIEW_ATTENTION_KEY = ['reviews', 'attention'] as const

type AttentionResponse = Awaited<ReturnType<typeof api.getReviewAttention>>

/** Nightly review statistics (per property, portfolio, monthly, year over year). */
export function useReviewStats() {
  return useQuery({
    queryKey: REVIEW_STATS_KEY,
    queryFn: () => api.getReviewStats(),
    staleTime: 5 * 60 * 1000,
  })
}

/** The "Needs attention" to-do, open items first. */
export function useReviewAttention() {
  return useQuery({
    queryKey: REVIEW_ATTENTION_KEY,
    queryFn: () => api.getReviewAttention(),
  })
}

/**
 * Complete / skip / reopen an attention item. Not optimistic (repo rule:
 * no guessed state, no guessed timestamps): the row updates from the item
 * the API returns, then the list refetches. The row's buttons are disabled
 * while the request is in flight, which is the feedback.
 */
export function useSetAttentionStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: AttentionStatus }) =>
      api.setReviewAttentionStatus(id, status),
    onSuccess: ({ item }) => {
      qc.setQueryData<AttentionResponse>(REVIEW_ATTENTION_KEY, previous =>
        previous
          ? { ...previous, items: previous.items.map(it => (it.id === item.id ? item : it)) }
          : previous
      )
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: REVIEW_ATTENTION_KEY })
    },
  })
}
